import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useWizard } from '../../context/WizardContext';
import { useLanguage } from '../../context/LanguageContext';
import { TOTAL_WIZARD_STEPS } from '../../constants/wizardSteps';
import { WizardNavigation } from './WizardButton';

const CURRENCIES = [
  { value: 'LKR', key: 'wizard.financialInfo.currencies.lkr', label: 'LKR - Sri Lankan Rupee' },
  { value: 'USD', key: 'wizard.financialInfo.currencies.usd', label: 'USD - US Dollar' },
  { value: 'EUR', key: 'wizard.financialInfo.currencies.eur', label: 'EUR - Euro' },
  { value: 'GBP', key: 'wizard.financialInfo.currencies.gbp', label: 'GBP - British Pound' },
  { value: 'CAD', key: 'wizard.financialInfo.currencies.cad', label: 'CAD - Canadian Dollar' },
  { value: 'AUD', key: 'wizard.financialInfo.currencies.aud', label: 'AUD - Australian Dollar' },
  { value: 'INR', key: 'wizard.financialInfo.currencies.inr', label: 'INR - Indian Rupee' }
];

const EDUCATION_BUDGETS = [
  { value: 'under_1m', key: 'wizard.financialInfo.budgets.lkr_under_1m', label: 'Under 1 Million LKR' },
  { value: '1m_3m', key: 'wizard.financialInfo.budgets.lkr_1m_3m', label: '1 - 3 Million LKR' },
  { value: '3m_5m', key: 'wizard.financialInfo.budgets.lkr_3m_5m', label: '3 - 5 Million LKR' },
  { value: '5m_7m', key: 'wizard.financialInfo.budgets.lkr_5m_7m', label: '5 - 7 Million LKR' },
  { value: '7m_10m', key: 'wizard.financialInfo.budgets.lkr_7m_10m', label: '7 - 10 Million LKR' },
  { value: '10m_plus', key: 'wizard.financialInfo.budgets.lkr_10m_plus', label: '10+ Million LKR' }
];

/**
 * FinancialInfo component for collecting user's financial information
 * @param {Object} props - Component props
 * @param {number} props.stepNumber - Current step number in wizard flow
 * @param {string} props.title - Step title displayed to user
 * @param {string} props.description - Optional description text
 * @param {Function} props.onNext - Callback when user proceeds to next step
 * @param {Function} props.onPrevious - Callback when user goes to previous step
 * @param {boolean} props.canGoNext - Whether next button should be enabled
 * @param {boolean} props.canGoPrevious - Whether previous button should be enabled
 * @param {string} props.className - Additional CSS classes
 */
const FinancialInfo = ({
  stepNumber = 8,
  totalSteps = TOTAL_WIZARD_STEPS,
  title = '',
  description = '',
  onNext,
  onPrevious,
  canGoNext = false,
  canGoPrevious = true,
  className = ''
}) => {
  const { state, actions } = useWizard();
  const { t } = useLanguage();

  // Auto-scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, []);
  const [formData, setFormData] = useState({
    educationBudget: state.financialInfo.educationBudget || '',
    currency: state.financialInfo.currency || 'LKR',
    financialNotes: state.financialInfo.financialNotes || ''
  });

  const [errors, setErrors] = useState({});

  const defaultTitle = t('wizard.financialInfo.title', 'Education Budget');
  const defaultDescription = t('wizard.financialInfo.description', 'Understanding your available education budget helps us recommend the most suitable immigration pathways and financial options.');
  const stepTitle = title || defaultTitle;
  const stepDescription = description || defaultDescription;

  const currencies = useMemo(() => CURRENCIES, []);
  const educationBudgets = useMemo(() => EDUCATION_BUDGETS, []);

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.educationBudget) {
      newErrors.educationBudget = t('wizard.financialInfo.errors.educationBudget.required', 'Please select your available education budget');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, t]);

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  }, [errors]);

  const handleNext = useCallback(() => {
    if (validateForm()) {
      // Update wizard state with financial info
      actions.updateFinancialInfo(formData);

      if (onNext) {
        onNext({
          financialInfo: formData
        });
      }
    }
  }, [formData, validateForm, actions, onNext]);

  const canProceed = useMemo(() => {
    const requiredFields = ['educationBudget'];
    return requiredFields.every(field => formData[field]) &&
      Object.keys(errors).every(field => !errors[field]);
  }, [formData, errors]);

  // Validate on mount to show any initial errors
  React.useEffect(() => {
    if (Object.keys(formData).some(key => formData[key])) {
      validateForm();
    }
  }, []);

  const getCurrencySymbol = useCallback((currency) => {
    const symbols = {
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'CAD': 'C$',
      'AUD': 'A$',
      'LKR': 'Rs',
      'INR': '₹'
    };
    return symbols[currency] || '$';
  }, []);

  const currencySymbol = getCurrencySymbol(formData.currency);

  return (
    <div className={`financial-info-step w-full mx-auto ${className}`}>
      {/* Header */}
      <div className="text-center mb-8">
        <div className="mb-2">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
            {t('wizard.common.stepIndicator', 'Step {{current}} of {{total}}', {
              current: stepNumber,
              total: totalSteps
            })}
          </span>
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-4">{stepTitle}</h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">{stepDescription}</p>
      </div>

      {/* Form */}
      <div className="card p-6 sm:p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Currency */}
          <div>
            <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-2">
              {t('wizard.financialInfo.fields.currency.label', 'Preferred Currency')}
            </label>
            <select
              id="currency"
              value={formData.currency}
              onChange={(e) => handleInputChange('currency', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            >
              {currencies.map(currency => (
                <option key={currency.value} value={currency.value}>{t(currency.key, currency.label)}</option>
              ))}
            </select>
          </div>

          {/* Education Budget */}
          <div>
            <label htmlFor="educationBudget" className="block text-sm font-medium text-gray-700 mb-2">
              {t('wizard.financialInfo.fields.educationBudget.label', 'Available Education Budget *')}
            </label>
            <select
              id="educationBudget"
              value={formData.educationBudget}
              onChange={(e) => handleInputChange('educationBudget', e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.educationBudget ? 'border-red-500' : 'border-gray-300'
                }`}
            >
              <option value="">{t('wizard.financialInfo.fields.educationBudget.placeholder', 'Select your education budget')}</option>
              {educationBudgets.map(budget => (
                <option key={budget.value} value={budget.value}>{t(budget.key, budget.label)}</option>
              ))}
            </select>
            {errors.educationBudget && (
              <p className="mt-1 text-sm text-red-600">{errors.educationBudget}</p>
            )}
          </div>

          {/* Financial Notes */}
          <div className="md:col-span-2">
            <label htmlFor="financialNotes" className="block text-sm font-medium text-gray-700 mb-2">
              {t('wizard.financialInfo.fields.financialNotes.label', 'Additional Financial Notes (Optional)')}
            </label>
            <textarea
              id="financialNotes"
              value={formData.financialNotes}
              onChange={(e) => handleInputChange('financialNotes', e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder={t('wizard.financialInfo.fields.financialNotes.placeholder', 'Any additional information about your financial situation, scholarships, or funding sources')}
            />
            <p className="mt-1 text-sm text-gray-500">
              {t('wizard.financialInfo.fields.financialNotes.helper', 'Let us know if you have scholarships, family support, or other funding arrangements')}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <WizardNavigation
        onPrevious={onPrevious}
        onNext={handleNext}
        previousLabel={t('wizard.common.previous', 'Previous')}
        nextLabel={t('wizard.common.next', 'Next Step')}
        canGoPrevious={canGoPrevious}
        canGoNext={canProceed}
        className="mt-8"
      />
    </div>
  );
};

export default FinancialInfo;
